import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { Loading } from '../Loading/Loading';
import { Pagination } from '../Pagination/Pagination';
const themes = [
    {
        name: 'wayste',
        container: 'border-none',
        headerRow: 'flex items-center py-2 border-b-0',
        headerCell: 'items-end px-4 text-neutral-500 py-0.5 text-xs font-semibold leading-3 tracking-wide uppercase border-r border-black border-opacity-10 h-fit',
        headerCellLast: 'border-r-0',
        regularRow: 'border-b-2 border-white bg-zinc-100',
        regularRowFirst: 'rounded-l-lg',
        regularRowLast: 'rounded-r-lg',
        regularCell: 'px-4 font-semibold text-zinc-700 text-xs',
        finalRow: 'border-b-0 bg-zinc-100',
        headerRowFirst: 'rounded-tl-lg',
        headerRowLast: 'rounded-tr-lg',
    },
];
// A single cell in the data grid
const Cell = ({ column, value, header, row, rowIndex, lastCell, isLastFrozenColumn, className, theme, }) => {
    if (column.hidden)
        return null;
    let content = value;
    if (!header && column.formatter) {
        content = column.formatter({ column, row, value, rowIndex });
    }
    return (_jsxs("div", { className: twMerge(`relative flex h-full w-full items-center px-2 text-sm text-gray-700`, header ? 'whitespace-nowrap font-semibold' : '', className || '', header && lastCell ? themes.find((t) => t.name === theme)?.headerCellLast : ''), style: {
            justifyContent: header ? column.headerAlign || 'flex-start' : column.align || 'flex-start',
            maxWidth: !header && column.width ? `${column.width}px` : '',
        }, children: [content, header && !lastCell && !isLastFrozenColumn && !className && (_jsx("div", { className: "absolute right-0 h-[15px] w-px rounded-3xl bg-slate-300" }))] }));
};
// A single row in the data grid
const GridRow = ({ row, columns, header, onRowClick, isOverflowingX, className, theme, rowIndex, }) => {
    const [hovered, setHovered] = useState(false);
    const [columnWidths, setColumnWidths] = useState(new Array(columns.length).fill(0));
    const cellRefs = useRef(new Array(columns.length).fill(null));
    // Function to calculate the width of each column
    const calculateColumnWidths = () => {
        // Calculate the width of each column after all resources have loaded
        const widths = cellRefs.current.map((ref) => ref?.offsetWidth || 0);
        setColumnWidths(widths);
    };
    // Effect for calculating column widths
    useEffect(() => {
        calculateColumnWidths();
        // Set up resize event listener
        const handleResize = () => calculateColumnWidths();
        window?.addEventListener('resize', handleResize);
        // Clean up event listener
        return () => window?.removeEventListener('resize', handleResize);
    }, []);
    useEffect(() => {
        // Calculate the width of each column
        const widths = cellRefs.current.map((ref) => ref?.getBoundingClientRect().width || 0);
        setColumnWidths(widths);
    }, [row]); // Re-calculate when the row data changes
    const getLeftPosition = (colIndex) => {
        // Sum the width of all columns before this one to get the left position
        return columnWidths.slice(0, colIndex).reduce((sum, width) => sum + width, 0);
    };
    return (_jsx(React.Fragment, { children: columns.map((col, index) => {
            const isLastFrozenColumn = col.frozen && (!columns[index + 1] || !columns[index + 1].frozen);
            // Determine the type of value based on the column key
            const valueKey = col.key;
            if (header) {
                return (_jsx("div", { ref: (el) => (cellRefs.current[index] = el), className: twMerge(`sticky top-0 z-[2] flex border-b border-b-gray-200 bg-white`, className || '', Boolean(onRowClick) && hovered ? 'cursor-pointer !bg-slate-100' : '', theme && index === 0 ? themes.find((t) => t.name === theme)?.headerRowFirst : '', theme && index === columns.length - 1 ? themes.find((t) => t.name === theme)?.headerRowLast : ''), onClick: () => {
                        if (onRowClick)
                            onRowClick(row, col);
                    }, onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false), style: col.frozen
                        ? {
                            position: 'sticky',
                            left: `${getLeftPosition(index)}px`,
                            backgroundColor: 'white',
                            zIndex: 3,
                            boxShadow: isLastFrozenColumn && isOverflowingX ? '10px 0 8px -2px rgba(0, 0, 0, 0.05)' : undefined,
                        }
                        : {}, children: _jsx(Cell, { column: col, rowIndex: -1, value: col.name, header: header, row: row, lastCell: index === columns.length - 1, isLastFrozenColumn: isLastFrozenColumn, theme: theme, className: theme ? themes.find((t) => t.name === theme)?.headerCell : '' }, index) }, col.key));
            }
            return (_jsx("div", { ref: (el) => (cellRefs.current[index] = el), className: twMerge(`flex border-b border-b-gray-200 bg-white`, className || '', Boolean(onRowClick) && hovered ? 'cursor-pointer !bg-slate-100' : '', theme && index === 0 ? themes.find((t) => t.name === theme)?.regularRowFirst : '', theme && index === columns.length - 1 ? themes.find((t) => t.name === theme)?.regularRowLast : ''), onClick: () => {
                    if (onRowClick)
                        onRowClick(row, col);
                }, onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false), style: col.frozen
                    ? {
                        position: 'sticky',
                        left: `${getLeftPosition(index)}px`,
                        zIndex: 1,
                        boxShadow: isLastFrozenColumn && isOverflowingX ? '10px 0 8px -2px rgba(0, 0, 0, 0.05)' : undefined,
                    }
                    : {}, children: _jsx(Cell, { column: col, value: row[valueKey], row: row, rowIndex: rowIndex, lastCell: index === columns.length - 1, isLastFrozenColumn: isLastFrozenColumn, className: theme ? themes.find((t) => t.name === theme)?.regularCell : '' }, index) }, col.key));
        }) }));
};
// The DataGrid component
export const DataGrid = ({ columns, rows = [], onRowClick, rowHeight = 40, pagination, loading, noDataFound, makeRow, noRowOverflow = false, theme, allowOverflow, }) => {
    const filteredColumns = columns.filter((col) => !col.hidden);
    const gridStyle = {
        display: 'grid',
        gridTemplateColumns: filteredColumns.map((col) => (col.width ? `${col.width}px` : 'minmax(max-content, auto)')).join(' '),
        gridTemplateRows: rows?.length
            ? `${rowHeight}px repeat(${rows.length},${noRowOverflow ? `${rowHeight}px` : `minmax(${rowHeight}px, min-content)`})`
            : `${rowHeight}px 1fr`,
    };
    const divRef = useRef(null);
    const [isOverflowingX, setIsOverflowingX] = useState(false);
    // Effect for checking overflow
    useEffect(() => {
        const checkOverflow = () => {
            const current = divRef.current;
            if (current) {
                setIsOverflowingX(current.scrollWidth > current.clientWidth);
            }
        };
        checkOverflow();
        // Optional: Re-check on window resize if the div's content might change
        window?.addEventListener('resize', checkOverflow);
        // Clean up event listener
        return () => window?.removeEventListener('resize', checkOverflow);
    }, [columns]); // Dependency on columns to re-check when columns change
    const handlePageChange = (page) => {
        if (!pagination)
            return;
        pagination.onPageChange(page);
        divRef.current?.scrollTo(0, 0);
    };
    return (_jsxs("div", { className: twMerge('relative flex w-full flex-1 flex-col rounded border border-gray-200', theme ? themes.find((t) => t.name === theme)?.container : '', allowOverflow ? 'h-full overflow-auto' : ''), children: [_jsxs("div", { className: `relative flex-1 overflow-auto ${theme ? '' : 'border-b border-b-gray-200'}`, style: gridStyle, ref: divRef, children: [_jsx(GridRow, { row: filteredColumns.reduce((obj, col) => ({ ...obj, [col.key]: col.name }), {}), header: true, columns: filteredColumns, isOverflowingX: isOverflowingX, theme: theme, className: theme ? themes.find((t) => t.name === theme)?.headerRow : '', rowIndex: -1 }), rows.length > 0 ? (rows.map((row, index) => {
                        const makeRowProps = makeRow?.(row) || {};
                        return (_jsx(GridRow, { row: row, columns: filteredColumns, onRowClick: onRowClick, isOverflowingX: isOverflowingX, theme: theme, className: theme
                                ? index === rows.length - 1
                                    ? themes.find((t) => t.name === theme)?.finalRow
                                    : themes.find((t) => t.name === theme)?.regularRow
                                : '', rowIndex: index, ...makeRowProps }, index));
                    })) : (_jsx("div", { style: {
                            gridArea: `2 / 1 / -1 / ${filteredColumns.length + 1}`,
                            // ignore the rowHeight
                            height: 'auto',
                        }, className: twMerge('flex w-full flex-col items-center justify-center', !noDataFound ? 'py-4 text-lg font-semibold text-gray-500' : ''), children: noDataFound || 'No data found' }))] }), pagination && (_jsx("div", { className: "flex justify-end", children: _jsx(Pagination, { ...pagination, onPageChange: (page) => handlePageChange(page), type: "ADVANCED" }) })), loading && (_jsxs("div", { className: "absolute inset-0 z-[3] flex w-full flex-1 flex-col items-center justify-center bg-white bg-opacity-50 backdrop-blur-sm", children: [_jsx(Loading, {}), _jsx("div", { children: "Loading..." })] }))] }));
};
//# sourceMappingURL=DataGrid.js.map